/* eslint-disable no-type-assertion/no-type-assertion -- AG-UI agent is duck-typed at runtime; the cast pins the subscriber shape. */

import { useCopilotChatInternal as useCopilotChat } from '@copilotkit/react-core'

// Local subset of `@ag-ui/client` types. Avoids pulling the AG-UI package
// as a direct dep — the subscriber is duck-typed at runtime anyway.
type TextMessageStartEvent = { messageId?: string }
type TextMessageContentEvent = { messageId?: string; delta?: string }
type TextMessageEndEvent = { messageId?: string }
type ToolCallStartEvent = {
  parentMessageId?: string
  toolCallId: string
  toolCallName: string
}
type ToolCallArgsEvent = { toolCallId: string }
type ToolCallEndEvent = { toolCallId: string }

type AgentSubscriber = {
  onTextMessageStartEvent?: (ctx: { event: TextMessageStartEvent }) => void
  onTextMessageContentEvent?: (ctx: {
    event: TextMessageContentEvent
    textMessageBuffer: string
  }) => void
  onTextMessageEndEvent?: (ctx: { event: TextMessageEndEvent }) => void
  onToolCallStartEvent?: (ctx: { event: ToolCallStartEvent }) => void
  onToolCallArgsEvent?: (ctx: {
    event: ToolCallArgsEvent
    partialToolCallArgs: Record<string, unknown>
    toolCallName: string
    toolCallBuffer: string
  }) => void
  onToolCallEndEvent?: (ctx: {
    event: ToolCallEndEvent
    toolCallName: string
    toolCallArgs: Record<string, unknown>
  }) => void
}

type AbstractAgent = {
  subscribe: (sub: AgentSubscriber) => { unsubscribe: () => void }
}
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type OrderedPart } from 'modules/ai/chat/protocol/turnUtils'

type OrderedPartsStore = Map<string, OrderedPart[]>

type OrderedMessagePartsContextValue = {
  /**
   * Returns the chronological parts captured for a given assistant message id,
   * or `null` if no events have been captured yet (the consumer should fall
   * back to its legacy expansion in that case).
   */
  getOrderedParts: (messageId: string) => OrderedPart[] | null
  /**
   * Bumps every time a new event is captured. Consumers depend on this in
   * `useMemo` deps so the memo re-runs without forcing the parts store
   * itself to be a useState (which would thrash React for every text delta).
   */
  version: number
}

const OrderedMessagePartsContext = createContext<OrderedMessagePartsContextValue | null>(null)

/**
 * Preserves the chronological order of streamed text + tool-call chunks.
 * CopilotKit collapses incoming events into `Message { content, toolCalls }`,
 * losing the position of each chunk. We subscribe to the raw AG-UI events
 * before the reducer runs and accumulate them keyed by message id. The
 * store is kept in a ref (not state) because events fire many times per
 * second during streaming and a useState rerender per delta would thrash
 * React; consumers depend on `version` instead.
 */
export const OrderedMessagePartsProvider = ({ children }: { children: ReactNode }) => {
  const { agent } = useCopilotChat()

  const storeRef = useRef<OrderedPartsStore>(new Map())
  // Tracks which message id is currently receiving text deltas, so
  // `onTextMessageContentEvent` can append to the right open part.
  const openTextMessageIdRef = useRef<string | null>(null)
  const [version, setVersion] = useState(0)

  const bump = useCallback(() => {
    setVersion((v) => v + 1)
  }, [])

  useEffect(() => {
    if (!agent) return

    const getParts = (messageId: string): OrderedPart[] => {
      let parts = storeRef.current.get(messageId)
      if (!parts) {
        parts = []
        storeRef.current.set(messageId, parts)
      }
      return parts
    }

    const subscriber: AgentSubscriber = {
      onTextMessageStartEvent: ({ event }: { event: TextMessageStartEvent }) => {
        const messageId = event.messageId
        if (!messageId) return
        const parts = getParts(messageId)
        parts.push({ kind: 'text', text: '' })
        openTextMessageIdRef.current = messageId
        bump()
      },

      onTextMessageContentEvent: ({
        event,
        textMessageBuffer,
      }: {
        event: TextMessageContentEvent
        textMessageBuffer: string
      }) => {
        // AG-UI's defaultApplyEvents invokes this subscriber BEFORE
        // appending the current delta to the stored message content, so
        // `textMessageBuffer` contains the text accumulated up to but
        // not including `event.delta`. We must combine both to capture
        // the full running text — otherwise the very last streamed
        // delta is never stored (no later content event arrives to
        // carry it in its buffer, and the end event does not refresh
        // the text), producing trailing 1–2 character truncation in
        // the rendered assistant message.
        const messageId = event.messageId ?? openTextMessageIdRef.current
        if (!messageId) return
        const delta = event.delta ?? ''
        const nextText = textMessageBuffer + delta
        const parts = getParts(messageId)
        const last = parts[parts.length - 1]
        if (last && last.kind === 'text') {
          last.text = nextText
        } else {
          // Defensive: missed the start event, open a new text part.
          parts.push({ kind: 'text', text: nextText })
          openTextMessageIdRef.current = messageId
        }
        bump()
      },

      onTextMessageEndEvent: ({ event }: { event: TextMessageEndEvent }) => {
        if (event.messageId === openTextMessageIdRef.current) {
          openTextMessageIdRef.current = null
        }
        bump()
      },

      onToolCallStartEvent: ({ event }: { event: ToolCallStartEvent }) => {
        // Tool calls live on the parent assistant message. AG-UI exposes
        // `parentMessageId`; if it's missing we fall back to whichever text
        // message is currently open.
        const messageId = event.parentMessageId ?? openTextMessageIdRef.current ?? null
        if (!messageId) return
        const parts = getParts(messageId)
        parts.push({
          kind: 'tool-call',
          toolCallId: event.toolCallId,
          toolName: event.toolCallName,
          args: {},
          complete: false,
        })
        bump()
      },

      onToolCallArgsEvent: ({
        event,
        partialToolCallArgs,
      }: {
        event: ToolCallArgsEvent
        partialToolCallArgs: Record<string, unknown>
        toolCallName: string
        toolCallBuffer: string
      }) => {
        const toolCallId = event.toolCallId
        // Find the matching tool-call part in any message and update its args.
        for (const parts of storeRef.current.values()) {
          for (let i = parts.length - 1; i >= 0; i--) {
            const p = parts[i]
            if (p && p.kind === 'tool-call' && p.toolCallId === toolCallId) {
              p.args = partialToolCallArgs ?? p.args
              bump()
              return
            }
          }
        }
      },

      onToolCallEndEvent: ({
        event,
        toolCallArgs,
      }: {
        event: ToolCallEndEvent
        toolCallName: string
        toolCallArgs: Record<string, unknown>
      }) => {
        const toolCallId = event.toolCallId
        for (const parts of storeRef.current.values()) {
          for (let i = parts.length - 1; i >= 0; i--) {
            const p = parts[i]
            if (p && p.kind === 'tool-call' && p.toolCallId === toolCallId) {
              p.args = toolCallArgs ?? p.args
              p.complete = true
              bump()
              return
            }
          }
        }
      },
    }

    const { unsubscribe } = (agent as AbstractAgent).subscribe(subscriber)
    return () => {
      unsubscribe()
    }
  }, [agent, bump])

  const getOrderedParts = useCallback((messageId: string): OrderedPart[] | null => {
    const parts = storeRef.current.get(messageId)
    if (!parts || parts.length === 0) return null
    // Defensive copy so consumers cannot mutate the store.
    return parts.slice()
  }, [])

  return (
    <OrderedMessagePartsContext.Provider value={{ getOrderedParts, version }}>
      {children}
    </OrderedMessagePartsContext.Provider>
  )
}

// Returns a no-op fallback when used outside the provider so callers
// that may or may not be wrapped keep working.
export const useOrderedMessageParts = (): OrderedMessagePartsContextValue => {
  const ctx = useContext(OrderedMessagePartsContext)
  if (ctx) return ctx
  return {
    getOrderedParts: () => null,
    version: 0,
  }
}
